import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  transfersAPI,
  type TransferDetailDto,
  type TransferRowDto,
  type TransferScope,
  type TransferStatus,
} from '@matix/api-client';
import { useLang } from '../../lib/language';
import { formatReceiptDateTime } from '../receipts/helpers';
import { useDebounced } from '../delivery-orders/useDeliveryOrders';
import { TransferDetailDialog } from './TransferDetailDialog';
import { NewTransferPanel } from './NewTransferPanel';
import { printTransferSlip } from './transferPrint';
import { StatusChip, storeName } from './shared';
import { PlusIcon, SearchIcon, TransferIcon } from '../../components/icons';

const TRANSFERS_KEY = 'inventory-transfers';

/**
 * The Transfers workspace.
 *
 * Two halves of one job. RAISE a request — pick a source and a destination,
 * scan or search the items, set quantities against the live source balance,
 * save (or, when the destination is this branch, transfer at once). RECEIVE
 * one — the destination branch confirms the goods are physically here, and
 * only then does the stock move. Both ends see the same list; the tabs cut
 * it to what is coming in, what is going out, or everything.
 *
 * Every document written is the legacy pair (Code 80 request, Code 81
 * goods), so the ERP's own transfer screens and stock reports read them.
 */
export function TransfersScreen(): JSX.Element {
  const { t, lang } = useLang();
  const qc = useQueryClient();

  const [scope, setScope] = useState<TransferScope>('all');
  const [status, setStatus] = useState<TransferStatus | 'all'>('pending');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);
  // True only for the dialog opened by saving a new request: receiving is
  // offered when the request is opened AGAIN from the list, never in the
  // same breath as raising it (it must exist as a request first).
  const [justCreated, setJustCreated] = useState(false);
  const [creating, setCreating] = useState(false);
  const debouncedSearch = useDebounced(search);
  const limit = 25;

  const stores = useQuery({
    queryKey: [TRANSFERS_KEY, 'stores'],
    queryFn: () => transfersAPI.stores(),
    staleTime: 5 * 60_000,
  });

  const list = useQuery({
    queryKey: [TRANSFERS_KEY, 'list', scope, status, debouncedSearch, page],
    queryFn: () =>
      transfersAPI.list({ scope, status, q: debouncedSearch || undefined, page, limit }),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  function invalidate(): void {
    void qc.invalidateQueries({ queryKey: [TRANSFERS_KEY] });
  }

  function onCreated(detail: TransferDetailDto): void {
    setCreating(false);
    invalidate();
    setJustCreated(true);
    setOpenId(detail.id);
  }

  const myStore = stores.data?.stores.find((s) => s.is_mine) ?? null;
  const canCreate = (stores.data?.stores.length ?? 0) >= 2;
  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="cc-screen tr-screen">
      <div className="tr-head">
        <div>
          <h1 className="tr-title">
            <TransferIcon size={22} /> {t({ en: 'Store transfers', ar: 'التحويلات المخزنية' })}
          </h1>
          <div className="tr-sub">
            {myStore
              ? t({ en: `Your store: ${storeName(myStore, lang)}`, ar: `مخزنك: ${storeName(myStore, lang)}` })
              : t({ en: 'Move goods between warehouses and receive what was sent to you.', ar: 'انقل البضاعة بين المخازن واستلم ما أُرسل إليك.' })}
          </div>
        </div>
        <button
          type="button"
          className="cc-btn cc-btn--primary tr-new-btn"
          onClick={() => setCreating(true)}
          disabled={!canCreate}
          title={canCreate ? undefined : t({ en: 'At least two active warehouses are needed', ar: 'يلزم مخزنان نشطان على الأقل' })}
        >
          <PlusIcon size={16} /> {t({ en: 'New transfer', ar: 'تحويل جديد' })}
        </button>
      </div>

      {stores.data && !canCreate && (
        <div className="cc-warn tr-warn">
          {t({
            en: 'This company has a single active warehouse, so there is nowhere to transfer to. Add a second warehouse in the ERP to use transfers.',
            ar: 'لدى هذه الشركة مخزن نشط واحد فقط، فلا يوجد مخزن آخر للتحويل إليه. أضف مخزناً ثانياً في النظام لاستخدام التحويلات.',
          })}
        </div>
      )}

      <div className="tr-filters">
        <div className="cc-tabs">
          {(['all', 'incoming', 'outgoing'] as TransferScope[]).map((s) => (
            <button
              key={s}
              type="button"
              className={`cc-tab${scope === s ? ' cc-tab--on' : ''}`}
              onClick={() => { setScope(s); setPage(1); }}
            >
              {s === 'all'
                ? t({ en: 'All', ar: 'الكل' })
                : s === 'incoming'
                  ? t({ en: 'Incoming', ar: 'واردة' })
                  : t({ en: 'Outgoing', ar: 'صادرة' })}
            </button>
          ))}
        </div>
        <select
          className="cc-select tr-status-select"
          value={status}
          onChange={(e) => { setStatus(e.target.value as TransferStatus | 'all'); setPage(1); }}
        >
          <option value="pending">{t({ en: 'Pending', ar: 'قيد الانتظار' })}</option>
          <option value="received">{t({ en: 'Received', ar: 'تم الاستلام' })}</option>
          <option value="cancelled">{t({ en: 'Cancelled', ar: 'ملغاة' })}</option>
          <option value="all">{t({ en: 'Any status', ar: 'كل الحالات' })}</option>
        </select>
        <div className="stock-balance-search tr-search">
          <SearchIcon size={16} />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={t({ en: 'Transfer number or reference…', ar: 'رقم التحويل أو المرجع…' })}
          />
        </div>
      </div>

      <div className="tr-table-wrap">
        <table className="cc-table tr-table">
          <thead>
            <tr>
              <th className="cc-num">#</th>
              <th>{t({ en: 'Date', ar: 'التاريخ' })}</th>
              <th>{t({ en: 'From', ar: 'من' })}</th>
              <th>{t({ en: 'To', ar: 'إلى' })}</th>
              <th className="cc-num">{t({ en: 'Items', ar: 'الأصناف' })}</th>
              <th className="cc-num">{t({ en: 'Qty', ar: 'الكمية' })}</th>
              <th>{t({ en: 'By', ar: 'بواسطة' })}</th>
              <th>{t({ en: 'Reference', ar: 'المرجع' })}</th>
              <th>{t({ en: 'Status', ar: 'الحالة' })}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && rows.length === 0 && (
              <tr><td colSpan={10} className="cc-table-empty">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</td></tr>
            )}
            {!list.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="cc-table-empty">
                  {status === 'pending'
                    ? t({ en: 'No pending transfers.', ar: 'لا توجد تحويلات قيد الانتظار.' })
                    : t({ en: 'Nothing here.', ar: 'لا يوجد شيء هنا.' })}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <TransferRow key={r.id} row={r} onOpen={() => { setJustCreated(false); setOpenId(r.id); }} />
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="tr-pager">
          <button type="button" className="cc-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            {t({ en: 'Previous', ar: 'السابق' })}
          </button>
          <span>{page} / {pages}</span>
          <button type="button" className="cc-btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            {t({ en: 'Next', ar: 'التالي' })}
          </button>
        </div>
      )}

      {openId != null && (
        <TransferDetailDialog id={openId} justCreated={justCreated} onClose={() => { setOpenId(null); setJustCreated(false); }} onChanged={invalidate} />
      )}
      {creating && stores.data && (
        <NewTransferPanel stores={stores.data} onClose={() => setCreating(false)} onCreated={onCreated} />
      )}
    </div>
  );
}

function TransferRow(props: { row: TransferRowDto; onOpen: () => void }): JSX.Element {
  const { row } = props;
  const { t, lang } = useLang();
  return (
    <tr className={`tr-row tr-row--${row.direction}`} onClick={props.onOpen}>
      <td className="cc-num">{row.entry_no}</td>
      <td className="tr-nowrap">{row.created_at ? formatReceiptDateTime(row.created_at) : '—'}</td>
      <td>
        {storeName(row.from_store, lang)}
        {row.direction === 'outgoing' && <span className="tr-mine">{t({ en: 'you', ar: 'أنت' })}</span>}
      </td>
      <td>
        {storeName(row.to_store, lang)}
        {row.direction === 'incoming' && <span className="tr-mine">{t({ en: 'you', ar: 'أنت' })}</span>}
      </td>
      <td className="cc-num">{row.line_count}</td>
      <td className="cc-num">{row.total_qty.toLocaleString(undefined, { maximumFractionDigits: 3 })}</td>
      <td>{row.created_by_name ?? '—'}</td>
      <td className="tr-ref">{row.reference_no ?? '—'}</td>
      <td>
        <StatusChip status={row.status} />
        {row.received_entry_no != null && <span className="tr-line-code"> #{row.received_entry_no}</span>}
      </td>
      <td className="tr-actions" onClick={(e) => e.stopPropagation()}>
        {row.can_receive && (
          <button type="button" className="cc-btn cc-btn--primary tr-btn-sm" onClick={props.onOpen}>
            {t({ en: 'Receive', ar: 'استلام' })}
          </button>
        )}
        <button
          type="button"
          className="cc-btn tr-btn-sm"
          title={t({ en: 'Print slip', ar: 'طباعة الإذن' })}
          onClick={() => void transfersAPI.get(row.id).then((d) => printTransferSlip(d, lang, 'roll'))}
        >
          🖨
        </button>
      </td>
    </tr>
  );
}
